/**
 * Genera byte casuali sicuri
 * @param byteCount numero di byte
 */
export function generateSecureRandomBytes(byteCount: number): Uint8Array {
    const arr = new Uint8Array(byteCount);
    crypto.getRandomValues(arr);
    return arr;
}

/**
 * Converte Base64 in Uint8Array
 */
export function base64ToBytes(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

/**
 * Converte Uint8Array in Base64 in modo efficiente
 * @param bytes Uint8Array
 */
export function bytesToBase64(bytes: Uint8Array): string {
    // Per array molto grandi, dividi in blocchi per evitare limitazioni di apply
    const chunkSize = 0x8000; // 32k blocchi
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        binary += String.fromCharCode(...chunk);
    }
    return btoa(binary);
}

/**
 * Converte un BN (big number) in un Uint8Array di lunghezza fissa, 
 * padding con zeri a sinistra se necessario.
 * @param bn Big number da convertire
 * @param length Lunghezza desiderata dell'array risultante
 * @returns Uint8Array rappresentante il big number
 */
export function bigIntToUint8Array(bn: any, length: number): Uint8Array {
    const hex = bn.toString(16).padStart(length * 2, "0");
    const arr = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        arr[i] = parseInt(hex.substr(i * 2, 2), 16);
    }
    return arr;
}

/**
 * Concatena più Uint8Array in un unico Uint8Array
 * @param arrays Array di Uint8Array da concatenare
 * @returns Uint8Array risultante dalla concatenazione di tutti gli array di input
 */
export function concatBuffers(...arrays: Uint8Array[]): Uint8Array {
    const total = arrays.reduce((sum, arr) => sum + arr.length, 0);
    const result = new Uint8Array(total);

    let offset = 0;
    for (const arr of arrays) {
        result.set(arr, offset);
        offset += arr.length;
    }

    return result;
}

/**
 * Ordina due Uint8Array in modo lessicografico (unsigned) e 
 * restituisce l'ordine corretto.
 * @param a Primo array
 * @param b Secondo array
 * @returns Array ordinato [min, max]
 */
export function sortUint8ArraysLex(a: Uint8Array, b: Uint8Array): [Uint8Array, Uint8Array] {
    for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return [a, b];
        if (a[i] > b[i]) return [b, a];
    }
    // Se uguali fino a minLen, ordina per lunghezza
    return a.length <= b.length ? [a, b] : [b, a];
}

/**
 * Confronta due Uint8Array in modo sicuro contro attacchi di timing. 
 * Restituisce true se sono uguali, false altrimenti.
 * @param a Primo array
 * @param b Secondo array
 * @returns true se gli array sono uguali, false altrimenti
 */
export function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false;
    let result = 0;
    for (let i = 0; i < a.length; i++) {
        result |= a[i] ^ b[i];
    }
    return result === 0;
}